const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

const authHeaders = (extra = {}) => ({
	"Content-Type": "application/json",
	Accept: "application/json",
	...(BACKEND_TOKEN ? { Authorization: `Bearer ${BACKEND_TOKEN}` } : {}),
	...extra,
});

const toNumber = (value) =>
	typeof value === "number" && !Number.isNaN(value) ? value : null;

export async function fetchDealerDetail(dealerId, signal) {
	const url = new URL(`dealers/${dealerId}/`, BACKEND_URL).toString();
	const res = await fetch(url, {
		method: "GET",
		headers: authHeaders(),
		signal,
	});
	if (!res.ok) throw new Error(`Dealer detail HTTP ${res.status}`);
	const json = await res.json();
	if (json?.success === false) {
		throw new Error(json.message || "Failed to load dealer details");
	}
	return json?.data ?? json ?? null;
}

export async function fetchDealersList(signal) {
	const url = new URL("dealers/", BACKEND_URL).toString();
	const res = await fetch(url, {
		method: "GET",
		headers: authHeaders(),
		signal,
	});
	if (!res.ok) throw new Error(`Dealer list HTTP ${res.status}`);
	const json = await res.json();
	if (json?.success === false) {
		throw new Error(json.message || "Failed to load dealers");
	}
	const raw = Array.isArray(json?.data)
		? json.data
		: Array.isArray(json?.data?.list)
		? json.data.list
		: [];
	return raw;
}

export async function fetchDealerScore(dealerId, signal) {
	const url = new URL(`dealers/${dealerId}/score/`, BACKEND_URL).toString();
	const res = await fetch(url, {
		method: "GET",
		headers: authHeaders(),
		signal,
	});
	if (!res.ok) throw new Error(`Dealer score HTTP ${res.status}`);
	const json = await res.json();
	const payload = json?.data ?? {};
	const baseScores =
		payload.scores_rolling_avg ??
		payload.scores ??
		payload.breakdown ??
		{};
	const weeklyRecords = collectDealerWeeklyRecords(payload);
	const lastFour =
		weeklyRecords.length > 4
			? weeklyRecords.slice(-4)
			: weeklyRecords.slice();

	const valueFromPayload = (key) => {
		if (typeof baseScores?.[key] === "number") return toNumber(baseScores[key]);
		if (
			payload.breakdown &&
			typeof payload.breakdown[key] === "number"
		) {
			return toNumber(payload.breakdown[key]);
		}
		if (typeof payload?.[key] === "number") return toNumber(payload[key]);
		return null;
	};

	const averageFor = (key) => {
		const values = lastFour
			.map((entry) => extractDealerScoreValue(entry, key))
			.filter((val) => typeof val === "number" && !Number.isNaN(val));
		if (values.length === 0) return null;
		const sum = values.reduce((acc, val) => acc + val, 0);
		return toNumber(sum / values.length);
	};

	const compositeFallback =
		valueFromPayload("composite_vehicle_score") ??
		valueFromPayload("composite_dealer_score") ??
		valueFromPayload("score");

	const breakdown = {
		performance_score:
			averageFor("performance_score") ??
			valueFromPayload("performance_score"),
		health_score:
			averageFor("health_score") ?? valueFromPayload("health_score"),
		energy_score:
			averageFor("energy_score") ?? valueFromPayload("energy_score"),
		composite_vehicle_score:
			averageFor("composite_vehicle_score") ??
			averageFor("composite_dealer_score") ??
			compositeFallback,
	};

	const weeksConsidered = lastFour
		.map((entry) => extractDealerWeekLabel(entry))
		.filter(Boolean);

	return {
		breakdown,
		weeks_considered: weeksConsidered,
		sample_count:
			typeof payload.sample_count === "number"
				? payload.sample_count
				: weeklyRecords.length || null,
		rolling_basis: payload.rolling_basis ?? null,
		window_weeks: lastFour.length || null,
	};
}

export async function fetchDealerInsightsLatest(dealerId, signal) {
	const url = new URL(
		`analytics/dealers/${dealerId}/insights/latest/`,
		BACKEND_URL
	).toString();
	const res = await fetch(url, {
		headers: authHeaders(),
		signal,
	});
	if (!res.ok) throw new Error(`Dealer insights latest HTTP ${res.status}`);
	const json = await res.json();
	if (json?.success === false) {
		throw new Error(json.message || "Failed to load dealer insights");
	}
	return json?.data ?? null;
}

export async function fetchDealerInsightsHistory(dealerId, signal) {
	const url = new URL(
		`analytics/dealers/${dealerId}/insights/history/`,
		BACKEND_URL
	).toString();
	const res = await fetch(url, {
		headers: authHeaders(),
		signal,
	});
	if (!res.ok) throw new Error(`Dealer insights history HTTP ${res.status}`);
	const json = await res.json();
	if (json?.success === false) {
		throw new Error(json.message || "Failed to load dealer insights history");
	}
	const results = Array.isArray(json?.data?.results)
		? json.data.results
		: Array.isArray(json?.results)
		? json.results
		: [];
	return results;
}

function collectDealerWeeklyRecords(payload = {}) {
	const buckets = [
		coerceDealerWeekly(payload?.weekly_scores),
		coerceDealerWeekly(payload?.scores_by_week),
		coerceDealerWeekly(payload?.weekly_breakdown),
		coerceDealerWeekly(payload?.history),
		coerceDealerWeekly(payload?.weeks),
	];

	const map = new Map();
	for (const bucket of buckets) {
		for (const entry of bucket) {
			if (!entry || typeof entry !== "object") continue;
			const key =
				entry.week ??
				entry.week_label ??
				entry.label ??
				entry.period ??
				entry.start ??
				`idx:${map.size}`;
			if (!map.has(key)) map.set(key, entry);
		}
	}

	return Array.from(map.values());
}

function coerceDealerWeekly(candidate) {
	if (!candidate) return [];
	if (Array.isArray(candidate)) return candidate;
	if (typeof candidate === "object") {
		return Object.entries(candidate).map(([label, value]) => {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				return { label, ...value };
			}
			return { label, value };
		});
	}
	return [];
}

function extractDealerScoreValue(entry, key) {
	if (!entry || typeof entry !== "object") return null;
	if (typeof entry[key] === "number") return entry[key];
	const sources = [
		entry.breakdown,
		entry.metrics,
		entry.scores,
		entry.score,
		entry.values,
		entry.data,
	];
	for (const source of sources) {
		if (
			source &&
			typeof source === "object" &&
			typeof source[key] === "number"
		) {
			return source[key];
		}
	}
	return null;
}

function extractDealerWeekLabel(entry) {
	if (!entry || typeof entry !== "object") return null;
	if (entry.week_label) return entry.week_label;
	if (entry.week) return entry.week;
	if (entry.label) return entry.label;
	if (entry.period) return entry.period;
	const range = dealerRangeLabel(entry);
	if (range) return range;
	return null;
}

function dealerRangeLabel(entry) {
	const start =
		entry.start ??
		entry.start_date ??
		entry.week_start ??
		entry.from ??
		entry.begin ??
		entry.period_start;
	const end =
		entry.end ??
		entry.end_date ??
		entry.week_end ??
		entry.to ??
		entry.until ??
		entry.period_end;
	if (start && end) return `${start} – ${end}`;
	return start ?? end ?? null;
}
